import React, { FC, useCallback, useEffect, useState } from 'react'
import { InsightViewProps } from './InsightView'
import { insightHasData, useResponsiveStyles } from '../../lib/utils'
import Button from '@mui/material/Button'
import { useStyles } from '../../lib/styles/universal'
import { makeStyles } from 'tss-react/mui'
import { colors } from '@hazadapt-git/public-core-base'
import {
    IoChevronBack,
    IoChevronForward,
    IoInformationCircle,
} from 'react-icons/io5'
import { BarGraphInsightView } from './BarGraphInsightView'
import { LineGraphInsightView } from './LineGraphInsightView'
import { DonutChartInsightView } from './DonutChartInsightView'
import { LineFillInsightView } from './LineFillInsightView'
import { MultiCounterInsightView } from './MultiCounterInsightView'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import { LoadingButton, Popover } from '../atoms'
import { useLocation } from 'react-router'
import { LoadingOverlay } from '../atoms/LoadingOverlay'
import { useInlineUpdateInsight } from '../../lib/hooks/useInlineUpdateInsight'
import { topAlignedPopoverPositioning } from '../../lib/entities/misc'

interface FullScreenInsightViewProps {
    currentInsightIndex: number
    insights: InsightViewProps[]
    onClose(): void
    selectedLocationString: string
    selectedDatesString: string
}

export const FullScreenInsightView: FC<FullScreenInsightViewProps> = ({
    currentInsightIndex,
    insights,
    onClose,
    selectedLocationString,
    selectedDatesString,
}) => {
    const [currentIndex, setCurrentIndex] =
        useState<number>(currentInsightIndex)
    const { classes } = useStyles()
    const { classes: localClasses } = useLocalStyles()
    const location = useLocation()
    const responsiveStyles = useResponsiveStyles()

    const insight = insights[currentIndex]

    const { loading } = useInlineUpdateInsight(
        insight.headline,
        insight.date_updated
    )

    const insightsWithData = insights.filter((i) => insightHasData(i))

    const handleNext = useCallback(() => {
        setCurrentIndex((prev) => (prev + 1) % insightsWithData.length)
    }, [insightsWithData.length])

    const handlePrevious = useCallback(() => {
        setCurrentIndex(
            (prev) =>
                (prev - 1 + insightsWithData.length) % insightsWithData.length
        )
    }, [insightsWithData.length])

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            switch (event.key) {
                case 'Escape':
                    onClose()
                    break
                case 'ArrowRight':
                    handleNext()
                    break
                case 'ArrowLeft':
                    handlePrevious()
                    break
                default:
                    break
            }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [onClose, handleNext, handlePrevious])

    const renderInsight = (insight: InsightViewProps) => {
        switch (insight.type) {
            case 'bar':
                return <BarGraphInsightView {...insight} />
            case 'line':
                return <LineGraphInsightView {...insight} />
            case 'donut':
                return <DonutChartInsightView {...insight} />
            case 'line-fill':
                return <LineFillInsightView {...insight} />
            case 'multi-counter':
                return <MultiCounterInsightView {...insight} />
            default:
                return null
        }
    }

    const getLocation = () => {
        if (location.pathname.includes('home')) return 'Home Page'
        if (location.pathname.includes('hazard'))
            return 'Hazard Guide Activity Overview'
        if (location.pathname.includes('prep'))
            return 'Prep Check Activity Overview'
    }

    return (
        <Box className={localClasses.container} style={responsiveStyles}>
            {/* HEADER ACTIONS */}
            <Box className={localClasses.headerActions}>
                <Typography
                    component="button"
                    className={localClasses.backButton}
                    onClick={onClose}
                >
                    <IoChevronBack style={{ paddingTop: '.1rem' }} />
                    {`Return to ${getLocation()}`}
                </Typography>
                <Box className={classes.buttonGroup}>
                    {insight.options.map((o) => (
                        <LoadingButton
                            key={o.label}
                            onClickAsync={o.onClickAsync}
                            endIcon={o.icon}
                        >
                            {o.label}
                        </LoadingButton>
                    ))}
                </Box>
            </Box>

            {/* INSIGHT HEADER */}
            <Box alignSelf={'center'} mb={2}>
                {/* TITLE & EXPLAINER */}
                <Box className={localClasses.title}>
                    <Typography
                        variant="h4"
                        component="h1"
                        className={localClasses.title}
                    >
                        {insight.headline}
                    </Typography>
                    {insight.explainer && (
                        <Box display="flex" alignItems="center">
                            <Popover
                                {...topAlignedPopoverPositioning}
                                icon={
                                    <IoInformationCircle
                                        color={colors.grays.NIMBUS}
                                    />
                                }
                                helpText={insight.explainer}
                            />
                        </Box>
                    )}
                </Box>
                {/* QUERY DETAILS */}
                <Typography
                    fontSize={'1rem'}
                    fontWeight={500}
                    component="h2"
                    align="center"
                >
                    {selectedLocationString} | {selectedDatesString}
                </Typography>
                {/* SUBLINE */}
                {insight.subline && (
                    <Typography textAlign={'center'}>
                        {insight.subline}
                    </Typography>
                )}
            </Box>

            <Grid container className={localClasses.insightContainer}>
                {/* PREV BUTTON */}
                <Grid
                    item
                    xs={'auto'}
                    className={localClasses.navButtonWrapper}
                >
                    <Button
                        onClick={handlePrevious}
                        startIcon={<IoChevronBack />}
                    >
                        Previous Graph
                    </Button>
                </Grid>
                {/* INSIGHT */}
                <Grid item xs className={localClasses.insight}>
                    {loading ? <LoadingOverlay loading={loading} /> : null}
                    <Box className={localClasses.graphWrapper}>
                        {renderInsight(insight)}
                    </Box>
                </Grid>
                {/* NEXT BUTTON */}
                <Grid
                    item
                    xs={'auto'}
                    className={localClasses.navButtonWrapper}
                >
                    <Button onClick={handleNext} endIcon={<IoChevronForward />}>
                        Next Graph
                    </Button>
                </Grid>
            </Grid>
        </Box>
    )
}

const useLocalStyles = makeStyles()({
    container: {
        backgroundColor: colors.grays.BLANC,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        left: 0,
        padding: '1rem',
        position: 'fixed',
        top: 0,
        zIndex: 2,
    },
    headerActions: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '1rem',
    },
    backButton: {
        alignItems: 'center',
        all: 'unset',
        color: colors.primary.CERULEAN,
        cursor: 'pointer',
        display: 'flex',
        fontWeight: '500',
        gap: '0.25rem',
        textDecoration: 'underline',
        '&:hover': {
            textDecorationThickness: '2px',
        },
        '&:focus': {
            outline: '2px solid',
            outlineColor: colors.primary.CERULEAN,
        },
    },
    title: {
        alignItems: 'center',
        display: 'flex',
        gap: '0.5rem',
        justifyContent: 'center',
    },
    insightContainer: {
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        padding: '.5rem',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        gap: '1rem',
        flexWrap: 'nowrap',
        flexDirection: 'row',
    },
    insight: {
        alignItems: 'center',
        display: 'flex',
        height: '100%',
        justifyContent: 'center',
        padding: '1rem',
        minWidth: 0,
    },
    navButtonWrapper: {
        whiteSpace: 'nowrap',
        minWidth: 'fit-content',
        flexShrink: 0,
        alignSelf: 'flex-end',
        marginBottom: '40vh',
    },
    graphWrapper: {
        alignItems: 'center',
        aspectRatio: '1 / 1',
        borderRadius: '0.5rem',
        boxShadow: '0 0.25rem 0.25rem 0 rgba(0, 0, 0, 0.35)',
        display: 'flex',
        justifyContent: 'center',
        height: '100%',
        maxWidth: '100%',
        padding: '1rem',
    },
})
